<script setup lang="ts">
import type {
	FileUploadSlots,
	FileUploadEmits as NuxtFileUploadEmits,
	FileUploadProps as NuxtFileUploadProps,
} from '@nuxt/ui';
import { reactiveOmit } from '@vueuse/core';
import { useForwardPropsEmits } from 'reka-ui';
import { useFileUploadLimit } from '#layers/assets/composables/use-file-upload-limit';

interface FileUploadProps extends Omit<NuxtFileUploadProps, 'multiple'> {
	multiple?: boolean;
	browseExisting?: boolean;
	browseDisabled?: boolean;
	uploadDisabled?: boolean;
	browseDisabledReason?: string;
	uploadDisabledReason?: string;
}

interface FileUploadEmits extends NuxtFileUploadEmits {
	browse: [];
}

const props = defineProps<FileUploadProps>();
const emits = defineEmits<FileUploadEmits>();
const slots = defineSlots<FileUploadSlots>();
const forwarded = useForwardPropsEmits(props, emits);

const { maxFileUploadSizeLabel } = useFileUploadLimit();

const proxySlots = reactiveOmit(slots, 'leading', 'actions');

// TODO Implement paste link
</script>

<template>
	<DFileUpload
		v-bind="forwarded"
		:ui="{
			base: 'min-h-30 text-sm',
			fileWrapper: 'flex-col gap-0.5',
			fileName: 'font-medium',
			fileSize: 'text-dimmed',
		}"
		size="sm"
	>
		<template #leading>
			<DIcon name="lucide:upload" size="20" />
		</template>
		<template #actions="{ open }">
			<div class="flex flex-col items-center justify-center gap-2 text-center">
				<i18n-t :keypath="browseExisting ? 'upload.select_or_drag_or_browse' : 'upload.select_or_drag'" tag="div">
					<template #browse>
						<DTooltip :disabled="!browseDisabled" :text="browseDisabledReason">
							<DButton
								class="p-0 underline"
								variant="link"
								:label="$t('upload.browse_existing')"
								color="neutral"
								:disabled="browseDisabled"
								@click.stop="$emit('browse')"
							/>
						</DTooltip>
					</template>
					<template #select>
						<DTooltip :disabled="!uploadDisabled" :text="uploadDisabledReason">
							<DButton
								class="p-0 underline"
								:label="$t('upload.select_from_device')"
								variant="link"
								color="neutral"
								:disabled="uploadDisabled"
								@click.stop="open()"
							/>
						</DTooltip>
					</template>
				</i18n-t>

				<p v-if="maxFileUploadSizeLabel" class="text-xs text-dimmed">
					{{ $t('upload.max_size_hint', { size: maxFileUploadSizeLabel }) }}
				</p>
			</div>
		</template>
		<template #file-leading="{ file }">
			<FileTypeIcon :file class="h-10" />
		</template>
		<template v-for="(_, name) in proxySlots" #[name]="slotData: any">
			<slot :name v-bind="slotData" />
		</template>
	</DFileUpload>
</template>
